import { useContext, useMemo } from 'react';
import clsx from 'clsx';

import { HydratedAttributeGroupsItem } from '@atom/types/inventory';

import AttributeDetailsContext, {
  AttributeView,
} from './AttributeDetailsContext';
import { getSplitArrays } from './attributeDetailUtilities';
import AttributeEditRow from './AttributeEditRow';
import AttributeGroupHeader from './AttributeGroupHeader';
import AttributeRow from './AttributeRow';

import classes from './attributeDetails.module.css';

interface Props {
  attributeGroup: HydratedAttributeGroupsItem;
}

const AttributeGroup = ({ attributeGroup }: Props) => {
  const { editGroup, expanded, splitView, view } = useContext(
    AttributeDetailsContext,
  );

  const isEditMode = editGroup?.id === attributeGroup?.id;
  const showAttributes = expanded.has(attributeGroup.id);

  const splitMappedAttributes = useMemo(() => {
    return getSplitArrays(attributeGroup);
  }, [splitView, attributeGroup]);

  const fullPageContent = (
    <div className={classes.attributeListFullContainer}>
      {isEditMode
        ? splitMappedAttributes.map(column => (
            <div className={classes.attributeSplitContainer}>
              {column.map(attribute => (
                <AttributeEditRow
                  key={attribute.id}
                  attributeGroup={attributeGroup}
                  attribute={attribute}
                />
              ))}
            </div>
          ))
        : splitMappedAttributes.map(column => (
            <div className={classes.attributeSplitContainer}>
              {column.map(attribute => (
                <AttributeRow
                  key={attribute.id}
                  attributeGroup={attributeGroup}
                  attribute={attribute}
                />
              ))}
            </div>
          ))}
    </div>
  );

  const halfPageContent = (
    <div
      className={clsx(
        classes.attributeListContainer,
        view === AttributeView.MAP_TRAY && classes.tray,
      )}
    >
      {isEditMode
        ? attributeGroup.attributes.map(attribute => (
            <AttributeEditRow
              key={attribute.id}
              attributeGroup={attributeGroup}
              attribute={attribute}
            />
          ))
        : attributeGroup.attributes.map(attribute => (
            <AttributeRow
              key={attribute.id}
              attributeGroup={attributeGroup}
              attribute={attribute}
            />
          ))}
    </div>
  );

  const content = splitView ? fullPageContent : halfPageContent;

  return (
    <>
      <AttributeGroupHeader attributeGroup={attributeGroup} />
      {showAttributes && content}
    </>
  );
};

export default AttributeGroup;
